import zlib from 'zlib';
import { parseString } from 'xml2js';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

var SHAPE;
(function (SHAPE) {
    SHAPE["ELLIPSE"] = "ellipse";
    SHAPE["POINT"] = "point";
    SHAPE["POLYGON"] = "polygon";
    SHAPE["RECTANGLE"] = "rectangle";
})(SHAPE || (SHAPE = {}));
var NODE_TYPE;
(function (NODE_TYPE) {
    NODE_TYPE["EDITOR_SETTINGS"] = "editorsettings";
    NODE_TYPE["GROUP"] = "group";
    NODE_TYPE["IMAGE_LAYER"] = "imagelayer";
    NODE_TYPE["LAYER"] = "layer";
    NODE_TYPE["OBJECT_GROUP"] = "objectgroup";
    NODE_TYPE["PROPERTIES"] = "properties";
    NODE_TYPE["TILESET"] = "tileset";
})(NODE_TYPE || (NODE_TYPE = {}));
var ENCODING;
(function (ENCODING) {
    ENCODING["BASE64"] = "base64";
    ENCODING["CSV"] = "csv";
})(ENCODING || (ENCODING = {}));
var COMPRESSION;
(function (COMPRESSION) {
    COMPRESSION["GZIP"] = "gzip";
    COMPRESSION["ZLIB"] = "zlib";
})(COMPRESSION || (COMPRESSION = {}));
var FLIPPED;
(function (FLIPPED) {
    FLIPPED[FLIPPED["DIAGONALLY"] = 536870912] = "DIAGONALLY";
    FLIPPED[FLIPPED["HORIZONTALLY"] = 2147483648] = "HORIZONTALLY";
    FLIPPED[FLIPPED["VERTICALLY"] = 1073741824] = "VERTICALLY";
})(FLIPPED || (FLIPPED = {}));

const isValidArray = (arr) => !!(arr && arr.length > 0);
const isDataURL = (s) => !!s.match(/^\s*data:([a-z]+\/[a-z]+(;[a-z-]+=[a-z-]+)?)?(;base64)?,[a-z0-9!$&',()*+,;=\-._~:@/?%\s]*\s*$/i);
const fetchDataURL = (data) => __awaiter(void 0, void 0, void 0, function* () {
    return new Promise(resolve => {
        fetch(data)
            .then(response => response.text())
            .then(body => resolve(body));
    });
});
const unpackTileBytes = (buf, expectedCount) => {
    const unpackedTiles = [];
    if (buf.length !== expectedCount) {
        throw new Error(`Expected ${expectedCount} bytes of tile data; received ${buf.length}`);
    }
    for (let i = 0; i < expectedCount; i += 4) {
        unpackedTiles.push(buf.readUInt32LE(i));
    }
    return unpackedTiles;
};
const getAttributes = ($) => $ && Object.entries($).map(([key, value]) => ({ [key]: value }));
const getProperties = (properties) => ({
    properties: (properties &&
        Object.assign({}, ...properties.map(({ property }) => Object.assign({}, ...property.map(({ $: { name, value }, _ }) => ({
            [name]: value || _
        })))))) ||
        null
});
const getFlips = (gid) => ({
    H: !!(gid & FLIPPED.HORIZONTALLY),
    V: !!(gid & FLIPPED.VERTICALLY),
    D: !!(gid & FLIPPED.DIAGONALLY)
});
const getTileId = (gid) => (gid &= ~(FLIPPED.HORIZONTALLY | FLIPPED.VERTICALLY | FLIPPED.DIAGONALLY));
const getObjectShape = (data) => (data.point && SHAPE.POINT) || (data.ellipse && SHAPE.ELLIPSE) || (data.polygon && SHAPE.POLYGON) || SHAPE.RECTANGLE;
const getValue = (value) => {
    if (value === 'true')
        return true;
    if (value === 'false')
        return false;
    if (`${value}`.match(/^[+-]?\d+(\.\d+)?$/g)) {
        const parsedValue = parseFloat(value);
        return !isNaN(parsedValue) ? parsedValue : value;
    }
    return value;
};
const parseXMLString = (xmlString) => __awaiter(void 0, void 0, void 0, function* () {
    const xml = isDataURL(xmlString) ? yield fetchDataURL(xmlString) : xmlString;
    return new Promise(resolve => parseString(xml, {
        explicitChildren: true,
        preserveChildrenOrder: true,
        attrValueProcessors: [(value) => getValue(value)]
    }, (error, tmx) => {
        if (error)
            throw error;
        resolve(tmx);
    }));
});
const parseObjectData = (data) => {
    const { $, properties, polygon, text } = data;
    const object = Object.assign(Object.assign(Object.assign({ shape: getObjectShape(data) }, (isValidArray(polygon) && {
        points: polygon[0].$.points.split(' ').map((point) => {
            const [x, y] = point.split(',');
            return [parseFloat(x), parseFloat(y)];
        })
    })), getProperties(properties)), ...getAttributes($));
    if (text) {
        object.text = text[0]._;
        object.properties = Object.assign({}, object.properties, text[0].$);
    }
    if (object.gid) {
        object.flips = getFlips(object.gid);
        object.gid = getTileId(object.gid);
    }
    return object;
};
const parseLayer = (layer) => {
    const { $, image, object, properties } = layer;
    return Object.assign(Object.assign(Object.assign(Object.assign({ type: layer['#name'], visible: 1 }, (isValidArray(image) && { image: Object.assign({}, ...getAttributes(image[0].$)) })), (isValidArray(object) && {
        objects: object.map((o) => parseObjectData(o))
    })), getProperties(properties)), ...getAttributes($));
};
const parseTileData = (data) => {
    const { $, animation, objectgroup } = data;
    const obj = isValidArray(objectgroup) && objectgroup[0].object;
    return Object.assign(Object.assign(Object.assign({}, (isValidArray(animation) && {
        animation: {
            frames: animation[0].frame.map(({ $ }) => Object.assign({}, ...getAttributes($)))
        }
    })), (isValidArray(obj) && {
        objects: obj.map((o) => parseObjectData(o))
    })), ...getAttributes($));
};
const parseGroup = (node, expectedCount, translateFlips) => __awaiter(void 0, void 0, void 0, function* () {
    return Object.assign(Object.assign({ type: node['#name'], layers: isValidArray(node.$$)
            ? yield Promise.all(node.$$.map((l) => parseNode(l, expectedCount, translateFlips)))
            : [] }, getProperties(node.properties)), ...getAttributes(node.$));
});
const parseTileset = (data) => {
    const { $, $$, tile } = data;
    const image = $$ && Object.assign({}, ...getAttributes($$[0].$));
    if (image) {
        const tiles = isValidArray(tile) && tile.map((t) => parseTileData(t));
        return Object.assign({}, ...getAttributes($), { image, tiles });
    }
    else {
        throw new Error('Tilesets should be embeded in a map file.');
    }
};
function parseTileLayer(node, expectedCount, translateFlips) {
    return __awaiter(this, void 0, void 0, function* () {
        const { data, properties } = node;
        if (!isValidArray(data)) {
            throw new Error('TMXLayer data corrupted!');
        }
        const newLayer = Object.assign(Object.assign(Object.assign(Object.assign({}, (translateFlips ? { flips: [] } : {})), { type: node['#name'], visible: 1 }), getProperties(properties)), ...getAttributes(node.$));
        const { _, $, tile } = data[0];
        /* XML Deprecated */
        if (tile) {
            newLayer.data = tile.map(({ $ }) => ($ && $.gid) || 0);
        }
        else {
            /* CSV, BASE64 */
            const { encoding, compression } = $;
            const layerData = _.trim();
            switch (encoding) {
                case ENCODING.CSV:
                    newLayer.data = layerData.split(',');
                    break;
                case ENCODING.BASE64:
                    const buffer = new Buffer(layerData, encoding);
                    switch (compression) {
                        case null:
                        case undefined:
                            newLayer.data = unpackTileBytes(buffer, expectedCount);
                            break;
                        case COMPRESSION.GZIP:
                        case COMPRESSION.ZLIB:
                            const decompress = compression === COMPRESSION.GZIP ? zlib.gunzip : zlib.inflate;
                            newLayer.data = yield new Promise((resolve, reject) => decompress(buffer, (err, buf) => {
                                if (err)
                                    reject(err);
                                resolve(unpackTileBytes(buf, expectedCount));
                            }));
                            break;
                        default:
                            throw new Error(`unsupported compression: ${compression}`);
                    }
            }
        }
        newLayer.data.map((gid, i) => {
            if (translateFlips) {
                newLayer.flips[i] = getFlips(gid);
                newLayer.data[i] = getTileId(gid);
            }
            else
                newLayer.data[i] = gid;
        });
        return newLayer;
    });
}
const parseNode = (node, expectedCount, translateFlips) => __awaiter(void 0, void 0, void 0, function* () {
    switch (node['#name']) {
        case NODE_TYPE.GROUP:
            return yield parseGroup(node, expectedCount, translateFlips);
        case NODE_TYPE.LAYER:
            return yield parseTileLayer(node, expectedCount, translateFlips);
        case NODE_TYPE.IMAGE_LAYER:
        case NODE_TYPE.OBJECT_GROUP:
            return parseLayer(node);
    }
    return;
});

function TMXParser(xmlString, translateFlips) {
    return __awaiter(this, void 0, void 0, function* () {
        const { map: { $, $$ } } = yield parseXMLString(xmlString);
        const data = Object.assign({
            layers: [],
            tilesets: [],
            backgroundcolor: '',
            height: 0,
            infinite: 0,
            nextlayerid: 0,
            nextobjectid: 0,
            orientation: '',
            renderorder: '',
            tiledversion: '',
            tileheight: 0,
            tilewidth: 0,
            version: 0,
            width: 0,
            properties: undefined
        }, ...getAttributes($));
        const expectedCount = data.width * data.height * 4;
        yield Promise.all($$.map((node, i) => __awaiter(this, void 0, void 0, function* () {
            switch (node['#name']) {
                case NODE_TYPE.PROPERTIES:
                    data.properties = Object.assign({}, ...node.$$.map(({ $: { name, value } }) => ({ [name]: value })));
                    break;
                case NODE_TYPE.EDITOR_SETTINGS:
                    data.editorsettings = Object.assign({}, ...node.$$.map((s) => ({ [s['#name']]: s.$ })));
                    break;
                case NODE_TYPE.TILESET:
                    data.tilesets.push(parseTileset(node));
                    break;
                case NODE_TYPE.GROUP:
                case NODE_TYPE.IMAGE_LAYER:
                case NODE_TYPE.LAYER:
                case NODE_TYPE.OBJECT_GROUP:
                    data.layers[i] = (yield parseNode(node, expectedCount, translateFlips));
                    break;
            }
        })));
        return Object.assign(Object.assign({}, data), { layers: Object.values(data.layers) });
    });
}

const tmx = (xmlString, translateFlips = false) => __awaiter(void 0, void 0, void 0, function* () { return yield TMXParser(xmlString, translateFlips); });

export { getFlips, getTileId, tmx };
